import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Link2, Loader2 } from "lucide-react";
import { Product } from "../types";
import { backupFigure, justInTimeSource, stockStatusWithBackup, supplyPoolFigure, withBackup } from "../utils/stockUtils";
import { backupJustInTimeLine, backupSourceName, backupSplitLine } from "../utils/backupStockCopy";
import { SupplyBadge } from "@/features/supplier-catalog/components/SupplyBadge";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import { StockAgeData } from "@/features/reporting/types";
import { ClientOrgBadge } from "@/components/ClientOrgBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { logger } from "@/lib/logger";
import { hexSlice } from "../utils/partnerFallback";
import { isInventoryMatrixProduct } from "../utils/matrixUtils";

interface InventoryTableMatrixProps {
  filteredProducts: Product[];
  showStockAge: boolean;
  stockAgeMap: Map<string, StockAgeData>;
  // BLU-3xx Phase 47.01.2-B-03: cross-org provenance for the matrix view.
  // The matrix is a SKU×size grid — there's no room for an Owner column or a
  // left-edge ribbon (would visually break the grid alignment). Instead,
  // when `showOwnerBadge` is true and a row contains client-owned cells, we
  // render a small ClientOrgBadge inside the first (color/title) cell.
  // Suppressed when the per-client filter narrows to one client (the sticky
  // banner above the table carries the meaning).
  showOwnerBadge?: boolean;
  ownOrgId?: string;
  /** BLU-1458: the caller's org name, for the back-up split ("0 owned by ..."). */
  ownOrgName?: string;
  orgIdToName?: Map<string, string>;
  /**
   * BLU-333: unlike the list view (`InventoryTableList` renders its own row
   * skeleton), the matrix had no loading gate at all — a location switch
   * kept rendering the PREVIOUS selection's grid (via the query's
   * `keepPreviousData` placeholder) with no indication the numbers belonged
   * to a different location. Pass the same `loading` the list view uses so
   * both surfaces stop showing stock levels attributed to the wrong scope.
   */
  loading?: boolean;
}

// Define the custom size order - Adult sizes first, then kids sizes
const sizeOrder = ["XS", "S", "M", "L", "XL", "2X", "3X", "4X", "4", "6", "8", "10", "12", "14", "16"];

// Helper function to sort sizes in the custom order
const sortSizes = (sizes: string[]): string[] => {
  return [...sizes].sort((a, b) => {
    const indexA = sizeOrder.indexOf(a);
    const indexB = sizeOrder.indexOf(b);

    // If both sizes are in the custom order list
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only a is in the custom order list
    if (indexA !== -1) {
      return -1;
    }

    // If only b is in the custom order list
    if (indexB !== -1) {
      return 1;
    }

    // If neither are in the custom order list, sort alphabetically
    return a.localeCompare(b);
  });
};

// Color name to hex mapping for better UX
const colorHexMap: Record<string, string> = {
  'black': '#000000',
  'natural': '#EBE9DF',
  'powder blue':'#0EA5E9',
  'charcoal': '#36454F',
  'lilac': '#9370DB',
  'pale blue': '#B0E0E6',
  'black ringer': '#000000',
  'red ringer': '#FF0000',
  'white': '#FFFFFF',
  'red': '#FF0000',
  'blue': '#0000FF',
  'green': '#008000',
  'yellow': '#FFFF00',
  'purple': '#800080',
  'pink': '#FFC0CB',
  'orange': '#FFA500',
  'brown': '#8B4513',
  'black stone': '#414040',
  'grey stone': '#535252',
  'grey': '#808080',
  'gray': '#808080',
  'navy': '#000080',
  'beige': '#F5F5DC',
  'cream': '#FFFDD0',
  'maroon': '#800000',
  'teal': '#008080',
  'olive': '#808000',
  'silver': '#C0C0C0',
  'gold': '#FFD700'
};

// Helper function to render color circle with special handling for ringers
const renderColorCircle = (color: string) => {
  const isRinger = color.toLowerCase().includes('ringer');

  if (isRinger) {
    // Extract the ringer color (black/red)
    const ringerColor = color.toLowerCase().includes('black') ? '#000000' : '#FF0000';
    return (
      <div
        className="w-4 h-4 rounded-full bg-white"
        style={{
          border: `2px solid ${ringerColor}`,
          boxSizing: 'border-box'
        }}
      />
    );
  } else {
    // Regular solid color circle
    return (
      <div
        className="w-4 h-4 rounded-full border border-border"
        style={{ backgroundColor: colorHexMap[color.toLowerCase()] || '#CCCCCC' }}
      />
    );
  }
};

// The matrix is apparel-only: unsized items and pre-printed finished units
// surface in the list view instead.
const groupTypeOf = (p: Product) => p.category.type || 'Unknown';
const rowKeyOf = (p: Product) => p.category.color || 'Unknown';

export function InventoryTableMatrix({
  filteredProducts,
  showStockAge,
  stockAgeMap,
  showOwnerBadge = false,
  ownOrgId,
  ownOrgName,
  orgIdToName,
  loading = false,
}: InventoryTableMatrixProps) {
  const { t } = useTranslation('inventory');

  const matrixProducts = useMemo(
    () => filteredProducts.filter(isInventoryMatrixProduct),
    [filteredProducts],
  );

  // Generate matrix view data grouped by type.
  //
  // BLU-3xx Phase 47.01.2-B-fix CRIT-4: at shared 3PL locations the
  // SAME `(type, color, size)` triple can be owned by multiple orgs
  // (e.g. Threadheads holds 23 of MZ-TEE-WHT-S; Mini Zali holds 12 of
  // their own MZ-TEE-WHT-S). The pre-fix builder wrote the cell as
  // `matrix[color][size] = product.stockLevel`, so the second product
  // overwrote the first → the cell silently displayed 12 instead of
  // 35. Fix: each cell stores `{ total, perOwner }`. The cell text is
  // the SUM; mixed-owner cells render a small amber dot indicator and
  // a tooltip listing the per-owner breakdown.
  type CellData = {
    total: number;
    /** Map of ownerOrganizationId → quantity. Undefined ownerId
     *  collapses to the empty-string key so single-tenant rows still
     *  aggregate correctly. */
    perOwner: Map<string, number>;
    /** BLU-1426: whose shared stock the total carries, when a client's
     *  alias row added its 3PL pool figure; null otherwise. */
    poolOwnerName: string | null;
    /** BLU-1426: a row added its own stockLevel (zero included) rather
     *  than a pool figure, so the total is not the pool alone. */
    hasOwnStock: boolean;
    /** BLU-1458: the back-up stock the cell's rows can draw on, each
     *  row's figure clamped at 0 before summing. `total` stays own stock;
     *  the cell shows the two together (`withBackup`). */
    backup: number;
    /** BLU-1458: whose back-up stock `backup` is. */
    backupOwners: Set<string>;
    /** BLU-1268: rows in the cell, and those that are Just in time (their
     *  own pair, or the 3PL's pool behind an alias). A just-in-time row adds
     *  nothing to `total`: its figure is the store constant, not a count. */
    rows: number;
    jitRows: number;
    /** BLU-1268: owners whose row here is Just in time (the mixed-owner tip). */
    jitOwners: Set<string>;
    /** BLU-1268: a row's back-up is the 3PL's just-in-time blank (unlimited). */
    backupJustInTime: boolean;
  };
  const matrixData = useMemo(() => {
    const groupedByType: Record<string, {
      // Row dimension: colours for apparel.
      rows: string[];
      sizes: string[];
      matrix: Record<string, Record<string, CellData>>;
      // First product seen per row — for the row image + cross-org owner derivation.
      sampleByRow: Record<string, Product>;
    }> = {};

    matrixProducts
      .forEach(product => {
        const type = groupTypeOf(product);
        const row = rowKeyOf(product);
        const size = product.category.size || 'Unknown';
        const ownerId = product.ownerOrganizationId ?? '';
        // BLU-1426: a client's alias row adds the 3PL's shared pool (as the
        // List View shows), never its own zero.
        const pool = supplyPoolFigure(product);
        // BLU-1268: a just-in-time row (own or pool) is not a count, so it adds nothing.
        const justInTime = justInTimeSource(product);
        const jitRow = justInTime === 'own' || justInTime === 'pool';
        const backupJustInTime = justInTime === 'backup';
        const qty = jitRow ? 0 : pool ?? product.stockLevel;
        const poolOwnerName = pool !== null ? product.supplyAlias?.ownerName || null : null;
        const backup = backupFigure(product);
        const backupOwner = backup > 0 || backupJustInTime ? backupSourceName(t, product) || null : null;

        if (!groupedByType[type]) {
          groupedByType[type] = {
            rows: [],
            sizes: [],
            matrix: {},
            sampleByRow: {},
          };
        }
        const group = groupedByType[type];

        if (!group.rows.includes(row)) group.rows.push(row);
        if (!group.sizes.includes(size)) group.sizes.push(size);
        if (!group.sampleByRow[row]) group.sampleByRow[row] = product;

        if (!group.matrix[row]) group.matrix[row] = {};
        const existing = group.matrix[row][size];
        if (existing) {
          existing.total += qty;
          existing.perOwner.set(
            ownerId,
            (existing.perOwner.get(ownerId) ?? 0) + qty,
          );
          existing.poolOwnerName ??= poolOwnerName;
          existing.hasOwnStock ||= pool === null;
          existing.backup += backup;
          if (backupOwner) existing.backupOwners.add(backupOwner);
          existing.rows += 1;
          if (jitRow) {
            existing.jitRows += 1;
            existing.jitOwners.add(ownerId);
          }
          existing.backupJustInTime ||= backupJustInTime;
        } else {
          group.matrix[row][size] = {
            total: qty,
            perOwner: new Map([[ownerId, qty]]),
            poolOwnerName,
            hasOwnStock: pool === null,
            backup,
            backupOwners: new Set(backupOwner ? [backupOwner] : []),
            rows: 1,
            jitRows: jitRow ? 1 : 0,
            jitOwners: new Set(jitRow ? [ownerId] : []),
            backupJustInTime,
          };
        }
      });

    // Sort rows (colours / design names) + sizes for consistent display
    Object.keys(groupedByType).forEach(type => {
      const typeData = groupedByType[type];
      if (typeData) {
        typeData.rows.sort((a, b) => a.localeCompare(b));
        typeData.sizes = sortSizes(typeData.sizes);
      }
    });

    // BLU-3xx Phase 47.01.2-B-fix2 NEW-M1: count mixed-owner CELLS
    // (not accumulation events). Pre-fix the counter `+= 1`'d on every
    // accumulation that landed on a multi-owner cell, so a (type, color,
    // size) tuple owned by 3 orgs would produce 2 increments instead of
    // 1. Now we make a single pass over the final cell map and count
    // cells whose `perOwner.size > 1`.
    let mixedOwnerCellCount = 0;
    for (const typeData of Object.values(groupedByType)) {
      for (const colorRow of Object.values(typeData.matrix)) {
        for (const cell of Object.values(colorRow)) {
          if (cell.perOwner.size > 1) mixedOwnerCellCount += 1;
        }
      }
    }

    // Surface a warn breadcrumb when the matrix detects mixed-owner
    // cells — useful for quantifying how often this case shows up at
    // shared 3PL locations once the feature is in operators' hands.
    if (mixedOwnerCellCount > 0) {
      logger.warn('inventory matrix view: mixed-owner cells detected', {
        mixedOwnerCellCount,
        productCount: matrixProducts.length,
      });
    }

    return groupedByType;
  }, [matrixProducts, t]);

  // BLU-333: render the loading state ahead of the grid rather than a stale
  // one — see the `loading` prop doc above.
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('common.loading')}
      </div>
    );
  }

  return (
    // BLU-3xx Phase 47.01.2-B-fix2 NEW-FM1: single TooltipProvider
    // hoisted to the matrix root. Pre-fix sweep, NEW-FM1 spotted a
    // regression where each mixed-owner cell opened its own provider
    // — same anti-pattern that MED-7 explicitly fixed in receive-
    // history rows. With ~hundreds of cells in a busy matrix, the
    // per-cell providers were measurably costlier to mount/unmount
    // than a single shared provider with bare <Tooltip> children.
    <TooltipProvider delayDuration={200}>
    <div className="space-y-8">
      {Object.entries(matrixData)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([type, data]) => (
        <div key={type} className="bg-card rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">{type}</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="border border-border p-3 bg-muted text-left font-medium">Color</th>
                  {data.sizes.map(size => (
                    <th key={size} className="border border-border p-3 bg-muted text-center font-medium min-w-16">
                      {size}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map(row => {
                  const sampleProduct = data.sampleByRow[row];

                  // BLU-3xx Phase 47.01.2-B-03: derive cross-org owner mix
                  // for this (type, color) row. If every cell in the row is
                  // owned by a single client, render that client's badge.
                  // Mixed-owner rows render no badge (per-cell labelling
                  // would clutter the grid; the per-client filter solves
                  // mixed-owner inspection).
                  const rowProducts = matrixProducts.filter(p =>
                    groupTypeOf(p) === type && rowKeyOf(p) === row
                  );
                  const ownerOrgIds = new Set(
                    rowProducts
                      .map(p => p.ownerOrganizationId)
                      .filter((id): id is string => !!id),
                  );
                  const singleOwner =
                    ownerOrgIds.size === 1 ? Array.from(ownerOrgIds)[0] : null;
                  const isClientOwnedRow =
                    showOwnerBadge && !!singleOwner && !!ownOrgId && singleOwner !== ownOrgId;
                  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.2: i18n the
                  // partner-fallback label so zh-CN users see the
                  // localised string. Reuses the shared
                  // `crossOrg.partnerFallbackHex` template + `hexSlice`
                  // util.
                  const clientOwnerName = isClientOwnedRow && singleOwner
                    ? (orgIdToName?.get(singleOwner)
                      ?? t('crossOrg.partnerFallbackHex', { hex: hexSlice(singleOwner) }))
                    : null;

                  return (
                    <tr key={row}>
                      <td className="border border-border p-3 bg-card">
                        <div className="flex items-center space-x-2">
                          <ProductImageWithHover
                            imageUrl={sampleProduct?.image_url}
                            productSku={`${type} - ${row}`}
                            size="medium"
                          />
                          {renderColorCircle(row)}
                          <span className="capitalize">{row}</span>
                          {clientOwnerName && (
                            <ClientOrgBadge
                              orgName={clientOwnerName}
                              className="ml-1"
                            />
                          )}
                        </div>
                      </td>
                      {data.sizes.map(size => {
                        const cell = data.matrix[row]?.[size];
                        const stockLevel = cell?.total ?? 0;
                        const isMixedOwner = (cell?.perOwner.size ?? 0) > 1;
                        // Representative product for stock-age + status colouring.
                        const product = matrixProducts.find(p =>
                          groupTypeOf(p) === type &&
                          rowKeyOf(p) === row &&
                          p.category.size === size
                        );

                        // Get stock age data if enabled
                        const stockAge = showStockAge && product ? stockAgeMap.get(product.id) : null;
                        const daysRemaining = stockAge?.days_remaining;

                        // BLU-1458: back-up stock is stock the client can
                        // sell, so the cell shows own plus back-up (clamped
                        // at 0) with a link mark, and the split on hover and
                        // in words for screen readers. Stock days are not
                        // stock, so never in that mode.
                        const cellBackup = !showStockAge && cell ? cell.backup : 0;
                        const shownStock = withBackup(stockLevel, cellBackup);
                        const backupLine = cellBackup > 0 && cell
                          ? backupSplitLine(t, {
                              backup: cellBackup,
                              owner: Array.from(cell.backupOwners).join(', '),
                              // A mixed-owner cell names the caller's share only.
                              own: isMixedOwner && ownOrgId ? (cell.perOwner.get(ownOrgId) ?? 0) : stockLevel,
                              ownOrgName,
                            })
                          : null;

                        // BLU-1268: Just in time is not a count. A cell whose
                        // every row is just in time, or whose back-up is, shows
                        // the mark in words in place of a number; a cell where
                        // only some rows are shows the counted ones with a clock.
                        const allJustInTime = !showStockAge && !!cell && cell.rows > 0 && cell.jitRows === cell.rows;
                        const someJustInTime = !showStockAge && !!cell && cell.jitRows > 0 && !allJustInTime;
                        const backupJustInTime = !showStockAge && !!cell && cell.backupJustInTime && !allJustInTime;
                        const jitBackupLine = backupJustInTime && cell
                          ? backupJustInTimeLine(t, {
                              owner: Array.from(cell.backupOwners).join(', '),
                              own: isMixedOwner && ownOrgId ? (cell.perOwner.get(ownOrgId) ?? 0) : stockLevel,
                              ownOrgName,
                            })
                          : null;

                        // Determine color based on stock age or stock level
                        let bgColor: string;
                        let textColor: string;
                        if (showStockAge || allJustInTime || backupJustInTime) {
                          // No background color for stock age - just plain text
                          bgColor = '';
                          textColor = 'text-foreground';
                        } else {
                          // Regular stock level color coding (BLU-1458: by
                          // the total, orange when back-up alone covers it)
                          const status = stockStatusWithBackup(stockLevel, cellBackup, product?.notify || null);
                          bgColor = status.bgColor;
                          textColor = status.textColor;
                        }

                        // BLU-3xx Phase 47.01.2-B-fix CRIT-4 + B-fix2
                        // NEW-FH1.2: build the per-owner breakdown for
                        // the tooltip when the cell is mixed-owner. All
                        // user-visible strings now flow through `t()`
                        // so zh-CN users see localised owner labels.
                        const ownerBreakdown = isMixedOwner && cell
                          ? Array.from(cell.perOwner.entries()).map(([ownerId, qty]) => {
                              const isOwn = ownerId === ownOrgId;
                              const label = isOwn
                                ? t('crossOrg.matrix.you')
                                : (orgIdToName?.get(ownerId)
                                  ?? (ownerId
                                    ? t('crossOrg.partnerFallbackHex', { hex: hexSlice(ownerId) })
                                    : t('crossOrg.matrix.unknownOwner')));
                              // BLU-1268: an owner whose row is just in time has no count to show.
                              return {
                                label,
                                qty: cell.jitOwners.has(ownerId) ? t('baseGarments.supply.justInTime') : String(qty),
                              };
                            })
                          : null;

                        // BLU-1426: a figure that is a client's alias pool
                        // alone says whose stock it is: a link mark, and the
                        // List View's line on hover and in words for screen
                        // readers. Stock days are not stock, so never in that
                        // mode.
                        const poolLine = !showStockAge && cell?.poolOwnerName && !cell.hasOwnStock
                          ? t('sharedSkus.supply.poolLine', { owner: cell.poolOwnerName })
                          : null;
                        // A pool line needs every row to be an alias, and an
                        // alias row never carries back-up: at most one of the two.
                        const markLine = poolLine ?? jitBackupLine ?? backupLine;

                        const cellInner = (
                          <span className="inline-flex items-center gap-1" title={markLine ?? undefined}>
                            {allJustInTime || backupJustInTime ? (
                              <SupplyBadge mode="just_in_time" />
                            ) : (
                            <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${bgColor} ${textColor}`}>
                              {showStockAge ? (
                                daysRemaining === null || daysRemaining === undefined || daysRemaining === 0 ? '-' :
                                Math.floor(daysRemaining)
                              ) : shownStock}
                            </span>
                            )}
                            {someJustInTime && (
                              <>
                                <Clock className="h-3.5 w-3.5 shrink-0 text-primary-strong" aria-hidden="true" />
                                <span className="sr-only"> {t('supply.partlyJustInTime')}</span>
                              </>
                            )}
                            {markLine && (
                              <>
                                <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                                <span className="sr-only"> {markLine}</span>
                              </>
                            )}
                            {isMixedOwner && (
                              <span
                                aria-hidden="true"
                                className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
                              />
                            )}
                          </span>
                        );

                        return (
                          <td key={`${row}-${size}`} className="border border-border p-3 text-center">
                            {ownerBreakdown ? (
                              // BLU-3xx Phase 47.01.2-B-fix2 NEW-FM1:
                              // bare <Tooltip> children of the matrix-
                              // root <TooltipProvider>; pre-fix opened
                              // a per-cell provider — same anti-pattern
                              // MED-7 fixed in receive-history rows.
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="cursor-help bg-transparent p-0"
                                    // BLU-1458: the label replaces the inner
                                    // sr-only words, so it carries the back-up line.
                                    aria-label={[
                                      t('crossOrg.matrix.mixedAriaLabel', {
                                        breakdown: ownerBreakdown
                                          .map((o) => `${o.qty} ${o.label}`)
                                          .join(', '),
                                      }),
                                      backupLine,
                                    ].filter(Boolean).join(' ')}
                                  >
                                    {cellInner}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <div className="text-xs space-y-0.5">
                                    <div className="font-medium">
                                      {t('crossOrg.matrix.mixedOwnersHeader')}
                                    </div>
                                    {ownerBreakdown.map((o) => (
                                      <div key={o.label}>
                                        {o.qty} {o.label}
                                      </div>
                                    ))}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              cellInner
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
    </TooltipProvider>
  );
}
